<script setup>
import Divider from 'primevue/divider'
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useIsMobile } from '@/shared/composables/use-is-mobile.composable'
import MenuItem from './components/menu-item.vue'
import SidebarHeader from './components/sidebar-header.vue'
import SidebarSearch from './components/sidebar-search.vue'
import { useDynamicMenu } from './composables/use-dynamic-menu'
import { useMenuAuthorization } from './composables/use-menu-authorization'
import { useMenuEnrichment } from './composables/use-menu-enrichment'
import { useMenuSearch } from './composables/use-menu-search'
import { useMenuTransform } from './composables/use-menu-transform'
import { usePinnedItems } from './composables/use-pinned-items'
import { useSearchHistory } from './composables/use-search-history'
import { useSidebarState } from './composables/use-sidebar-state'
import { MENU_ITEM_STYLES, SIDEBAR_MOBILE_BREAKPOINT, SIDEBAR_WIDTHS, SPACING, TRANSITION_DURATIONS } from './constants/sidebar.constants'

const { filterAuthorizedItems } = useMenuAuthorization()
const { enrichModulesWithDashboards } = useMenuEnrichment()
const { transformMenuItems } = useMenuTransform()
const { collapsed, toggleSidebar, setSidebarCollapsed, expandedKeys, handleItemClick, isItemActive, hasActiveChild, setMenuItems } = useSidebarState()

const { isMobile } = useIsMobile(SIDEBAR_MOBILE_BREAKPOINT)

watch(isMobile, (isNarrow) => {
  if (isNarrow)
    setSidebarCollapsed(true)
})

const { loadDynamicMenuItems } = useDynamicMenu()

const menuItems = computed(() => loadDynamicMenuItems())

const filteredMenuItems = computed(() => {
  const filtered = {
    core: [],
    modules: [],
  }

  const items = menuItems.value

  if (items.core) {
    filtered.core = items.core
      .map(item => filterAuthorizedItems(item))
      .filter(Boolean)
  }

  if (items.modules) {
    const authorizedModules = items.modules
      .map(item => filterAuthorizedItems(item))
      .filter(Boolean)

    filtered.modules = enrichModulesWithDashboards(authorizedModules)
  }

  return filtered
})

const transformedMenuItems = computed(() => {
  return transformMenuItems(filteredMenuItems.value)
})

watch(transformedMenuItems, (items) => {
  setMenuItems(items)
}, { immediate: true })

const router = useRouter()
const { recordVisit, getSuggestion } = useSearchHistory()
const { isPinned, togglePin, buildPinnedItems } = usePinnedItems()
const pinnedItems = buildPinnedItems(transformedMenuItems)
const { searchQuery, hasQuery, focusedIndex, clearSearch, applySearch, buildKeyboardHandler, isFocused } = useMenuSearch()
const visibleMenuItems = applySearch(transformedMenuItems)
const searchReference = ref(null)
const pendingResizeTimers = []
const onSearchKeydown = buildKeyboardHandler(visibleMenuItems, router)
const searchSuggestion = computed(() => getSuggestion(searchQuery.value))

function notifyLayoutResize() {
  const dispatch = () => window.dispatchEvent(new Event('resize'))

  dispatch()
  requestAnimationFrame(dispatch)
  requestAnimationFrame(() => requestAnimationFrame(dispatch))

  const timer = window.setTimeout(dispatch, 320)
  pendingResizeTimers.push(timer)
}

function onAcceptSuggestion(value) {
  searchQuery.value = value
}

watch(searchQuery, () => {
  focusedIndex.value = -1
})

async function expandAndFocusSearch() {
  toggleSidebar()
  await nextTick()
  searchReference.value?.focus()
}

watch(collapsed, (isCollapsed) => {
  if (isCollapsed)
    clearSearch()

  notifyLayoutResize()
})

function onGlobalKeydown(event) {
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault()
    expandAndFocusSearch()
  }
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown))
onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  pendingResizeTimers.forEach(timer => window.clearTimeout(timer))
  pendingResizeTimers.length = 0
})

provide('expandedKeys', expandedKeys)
provide('handleItemClick', handleItemClick)
provide('isItemActive', isItemActive)
provide('hasActiveChild', hasActiveChild)
provide('searchQuery', searchQuery)
provide('isFocused', item => isFocused(item, visibleMenuItems))
provide('isPinned', isPinned)
provide('togglePin', togglePin)
provide('recordVisit', recordVisit)
</script>

<template>
  <div
    id="app-sidebar"
    class="dss-sidebar border-r border-r-dss-surface-200 h-full min-h-0 flex flex-col transition-[width] ease-in-out box-border"
    :class="[
      collapsed ? SIDEBAR_WIDTHS.COLLAPSED : SIDEBAR_WIDTHS.EXPANDED,
      TRANSITION_DURATIONS.SIDEBAR,
    ]"
  >
    <sidebar-header />

    <div
      class="shrink-0 border-b border-b-dss-surface-200"
      :class="collapsed ? 'px-3 py-2.5' : 'px-3 py-3'"
    >
      <button
        v-if="collapsed"
        v-tooltip.right="{ value: $t('menu.search'), class: 'font-hellix text-xs' }"
        type="button"
        class="flex items-center justify-center rounded-md text-sm transition-colors hover:bg-dss-surface-275 px-2.5 text-dss-secondary-650 cursor-pointer"
        :class="[MENU_ITEM_STYLES.HEIGHT, MENU_ITEM_STYLES.BUTTON_WIDTH]"
        @click="expandAndFocusSearch"
      >
        <i class="pi pi-search text-sm" />
      </button>

      <transition
        v-else
        enter-active-class="transition-opacity duration-150 ease-in"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-100 ease-out"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        appear
      >
        <sidebar-search
          ref="searchReference"
          v-model="searchQuery"
          :suggestion="searchSuggestion"
          @keydown="onSearchKeydown"
          @accept-suggestion="onAcceptSuggestion"
        />
      </transition>
    </div>

    <div
      class="overflow-y-auto flex-1 flex flex-col"
      :class="[SPACING.MENU_PADDING, SPACING.MENU_GAP]"
    >
      <template v-if="!hasQuery && pinnedItems.length > 0">
        <button
          v-if="collapsed"
          v-tooltip.right="{ value: $t('menu.pinned'), class: 'font-hellix text-xs' }"
          type="button"
          class="flex items-center justify-center rounded-md text-sm transition-colors hover:bg-dss-surface-275 px-2.5 text-dss-secondary-650 cursor-pointer"
          :class="[MENU_ITEM_STYLES.HEIGHT, MENU_ITEM_STYLES.BUTTON_WIDTH]"
          @click="toggleSidebar"
        >
          <i class="pi pi-bookmark-fill text-sm opacity-60" />
        </button>

        <template v-else>
          <menu-item
            v-for="item in pinnedItems"
            :key="`pinned-${item.key}`"
            :item="item"
            :level="0"
            :collapsed="collapsed"
          />
          <Divider class="my-0! -mx-3! w-[calc(100%+1.5rem)]!" />
        </template>
      </template>

      <template
        v-for="item in visibleMenuItems"
        :key="item.key"
      >
        <Divider
          v-if="item.separator"
          class="my-0! -mx-3! w-[calc(100%+1.5rem)]!"
        />
        <menu-item
          v-else
          :item="item"
          :level="0"
          :collapsed="collapsed"
        />
      </template>

      <div
        v-if="hasQuery && visibleMenuItems.length === 0"
        class="flex flex-col items-center gap-2 py-6 text-dss-surface-400"
      >
        <i class="pi pi-search text-xl" />
        <span class="text-xs text-center font-hellix">
          {{ $t('menu.search-empty', searchQuery) }}
        </span>
      </div>
    </div>
  </div>
</template>
